'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ParticipantKind, RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import { ATTR_ALLOWED_SURFACES, ATTR_ENABLED, RPC_NOTIFY, SCREENSHARE_PROTOCOL_VERSION, parseAllowedSurfaces, } from '@/lib/screenshare-protocol';
/**
 * TLZ-561. The agent side of the screenshare conversation, as seen from the widget:
 * whether there is an agent that can receive a share, where to reach it, and how to tell
 * it that a share has ended.
 *
 * It is deliberately separate from `useScreenshareSession`, which owns the caller's own
 * state. The session hook reports a stop exactly once, from its single
 * `LocalTrackUnpublished` listener; this is what turns that one report into one
 * `screenshare.notify`.
 */
/** Bounded: the notification is best-effort and must never hold anything up. */
const NOTIFY_RESPONSE_TIMEOUT_MS = 5000;
/** The worker joins as an agent-kind participant. */
export function isAgentParticipant(participant) {
    if (!participant) {
        return false;
    }
    return participant.isAgent === true || participant.kind === ParticipantKind.AGENT;
}
/**
 * An agent that is not merely present but is listening for a share. Without this second
 * condition the caller could start a share nobody receives, which publishes a screen
 * track against no consent record at all.
 */
export function agentCanReceiveShare(participant) {
    var _a;
    return isAgentParticipant(participant) && ((_a = participant === null || participant === void 0 ? void 0 : participant.attributes) === null || _a === void 0 ? void 0 : _a[ATTR_ENABLED]) === 'true';
}
/**
 * Watches the room for an agent that can receive a share.
 *
 * `ParticipantAttributesChanged` matters as much as `ParticipantConnected` here: the
 * agent publishes `telzino.screenshare.enabled` AFTER it joins, so a connect-only
 * listener would show the control to nobody, or never show it at all.
 */
export function useScreenshareAgent() {
    const room = useRoomContext();
    const [state, setState] = useState({
        agentReady: false,
        agentIdentity: null,
        allowedSurfaces: parseAllowedSurfaces(undefined),
    });
    useEffect(() => {
        if (!room) {
            return;
        }
        const evaluate = () => {
            var _a;
            const participants = room.remoteParticipants
                ? Array.from(room.remoteParticipants.values())
                : [];
            const ready = participants.find((participant) => agentCanReceiveShare(participant));
            const present = ready !== null && ready !== void 0 ? ready : participants.find((participant) => isAgentParticipant(participant));
            const allowedSurfaces = parseAllowedSurfaces((_a = ready === null || ready === void 0 ? void 0 : ready.attributes) === null || _a === void 0 ? void 0 : _a[ATTR_ALLOWED_SURFACES]);
            setState((previous) => {
                var _a;
                const agentReady = Boolean(ready);
                // A departed agent leaves its identity behind on purpose; see `agentIdentity`.
                const agentIdentity = (_a = present === null || present === void 0 ? void 0 : present.identity) !== null && _a !== void 0 ? _a : previous.agentIdentity;
                if (previous.agentReady === agentReady &&
                    previous.agentIdentity === agentIdentity &&
                    previous.allowedSurfaces.join(',') === allowedSurfaces.join(',')) {
                    return previous;
                }
                return { agentReady, agentIdentity, allowedSurfaces };
            });
        };
        evaluate();
        room.on(RoomEvent.Connected, evaluate);
        room.on(RoomEvent.ParticipantConnected, evaluate);
        room.on(RoomEvent.ParticipantDisconnected, evaluate);
        room.on(RoomEvent.ParticipantAttributesChanged, evaluate);
        return () => {
            room.off(RoomEvent.Connected, evaluate);
            room.off(RoomEvent.ParticipantConnected, evaluate);
            room.off(RoomEvent.ParticipantDisconnected, evaluate);
            room.off(RoomEvent.ParticipantAttributesChanged, evaluate);
        };
    }, [room]);
    return state;
}
/**
 * Who ended the share. This is the attribution the track events cannot express: an
 * unpublish looks identical whoever caused it, and the audit row needs to say which.
 */
const STOP_INITIATOR = {
    caller_stop: 'caller',
    browser_stop: 'caller',
    agent_end: 'agent',
    agent_left: 'agent',
};
export function stopNotifyPayload(reason) {
    var _a;
    return {
        v: SCREENSHARE_PROTOCOL_VERSION,
        event: 'stopped',
        // A reason added to the protocol later is attributed to the caller rather than
        // silently to the agent: the caller is the party a wrong attribution wrongs.
        initiated_by: (_a = STOP_INITIATOR[reason]) !== null && _a !== void 0 ? _a : 'caller',
        reason,
    };
}
/**
 * The one place a `screenshare.notify` leaves the widget. Both notifiers share it so the
 * wire behaviour -- destination, method, payload encoding and timeout -- cannot drift
 * between the two events the agent hears about.
 */
function useNotifySender() {
    const room = useRoomContext();
    const { agentIdentity } = useScreenshareAgent();
    const identityRef = useRef(agentIdentity);
    useEffect(() => {
        identityRef.current = agentIdentity;
    }, [agentIdentity]);
    return useCallback((payload, messages) => {
        const destinationIdentity = identityRef.current;
        if (!room || !destinationIdentity) {
            messages.noAgent();
            return;
        }
        void (async () => {
            try {
                await room.localParticipant.performRpc({
                    destinationIdentity,
                    method: RPC_NOTIFY,
                    payload: JSON.stringify(payload),
                    responseTimeout: NOTIFY_RESPONSE_TIMEOUT_MS,
                });
            }
            catch (err) {
                console.warn(messages.failure, err);
            }
        })();
    }, [room]);
}
/**
 * The `onStopped` handler for `useScreenshareSession`: one ended share, one
 * `screenshare.notify`.
 *
 * Fire-and-forget by design. Nothing in this feature may end or degrade the audio
 * session, and the two cases where the RPC cannot succeed -- the agent has left, or the
 * agent has no handler registered -- are both cases where there is nothing to do about
 * it but say so in the console.
 */
export function useScreenshareStopNotifier() {
    const send = useNotifySender();
    return useCallback((reason) => {
        send(stopNotifyPayload(reason), {
            noAgent: () => console.warn('[screenshare] no agent to notify that sharing stopped', reason),
            failure: '[screenshare] could not tell the agent that sharing stopped',
        });
    }, [send]);
}
/**
 * The `consent` notification for a share the CALLER started from the widget's own
 * button. Pressing the button is the agreement; the browser picker then decides the
 * outcome, and it is the OUTCOME that is announced -- after the track is published for
 * `granted`, so the agent never waits on a share the caller dismissed in the picker.
 * The agent records it as the share's audit row; without it the share is recorded as
 * "pre_existing" with an unknown surface.
 */
export function callerConsentNotifyPayload(response) {
    return Object.assign(Object.assign(Object.assign({ v: SCREENSHARE_PROTOCOL_VERSION, event: 'consent', initiated_by: 'caller', result: response.result }, (response.surface ? { surface: response.surface } : {})), (response.track_sid ? { track_sid: response.track_sid } : {})), (response.reason ? { reason: response.reason } : {}));
}
/**
 * The `onCallerConsent` handler for `useScreenshareSession`: one caller-initiated share
 * outcome, one `screenshare.notify`. Same fire-and-forget rules as the stop notifier:
 * nothing here may end or degrade the audio session.
 */
export function useScreenshareCallerConsentNotifier() {
    const send = useNotifySender();
    return useCallback((response) => {
        send(callerConsentNotifyPayload(response), {
            noAgent: () => console.warn('[screenshare] no agent to notify about the caller-started share', response.result),
            failure: '[screenshare] could not tell the agent about the caller-started share',
        });
    }, [send]);
}
export default useScreenshareAgent;
